import { useState, useEffect, useRef, useCallback } from "react";
import api from "../lib/api";
import { useCurrency } from "../contexts/CurrencyContext";
import { X, Upload, Trash2, MapPin, CalendarDays, Building, Image as ImageIcon, Plus, Check, Circle, CircleCheck, CircleDot, ArrowLeftRight, FileText, Download, Eye, FileUp, Sparkles, Pencil } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "./ui/sonner";
import ConfirmDialog from "./ConfirmDialog";
import AddAssetDialog from "./AddAssetDialog";
import { format as formatDate } from "date-fns";

const locationLabels = { dubai: "Dubai, UAE", north_cyprus: "North Cyprus", germany: "Germany" };

const BUNDESLAND_LABELS = {
  bayern: "Bayern", sachsen: "Sachsen", hamburg: "Hamburg",
  baden_wuerttemberg: "Baden-W\u00fcrttemberg", bremen: "Bremen",
  niedersachsen: "Niedersachsen", rheinland_pfalz: "Rheinland-Pfalz",
  sachsen_anhalt: "Sachsen-Anhalt", mecklenburg_vorpommern: "Mecklenburg-Vorpommern",
  berlin: "Berlin", hessen: "Hessen", brandenburg: "Brandenburg",
  nrw: "Nordrhein-Westfalen", saarland: "Saarland",
  schleswig_holstein: "Schleswig-Holstein", thueringen: "Th\u00fcringen",
};

export default function AssetDetailModal({ open, asset, onClose, onRefresh }) {
  const { mode, fmt: globalFmt, display, SYMBOLS } = useCurrency();
  const [localOverride, setLocalOverride] = useState(null); // null = use global, "EUR" or "LOCAL" = override
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [msForm, setMsForm] = useState({ name: "", date: "", amount: "", percentage: "", trigger: "", is_paid: false });
  const [msLoading, setMsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docUploading, setDocUploading] = useState(false);
  const [unlinkDocId, setUnlinkDocId] = useState(null);
  const [extractingDocId, setExtractingDocId] = useState(null);
  const [confirmClearPlan, setConfirmClearPlan] = useState({ open: false, onConfirm: null });
  const [editingRental, setEditingRental] = useState(false);
  const [rentalDraft, setRentalDraft] = useState("");
  const [editingRunCosts, setEditingRunCosts] = useState(false);
  const [runCostsDraft, setRunCostsDraft] = useState("");
  const [remindersEnabled, setRemindersEnabled] = useState(false);
  const [remindersBusy, setRemindersBusy] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [editAssetOpen, setEditAssetOpen] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const docFileRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    if (!asset?.id) return;
    setDocsLoading(true);
    try {
      const { data } = await api.get(`/assets/${asset.id}/documents`);
      setDocs(data);
    } catch (err) {
      console.error(err);
    } finally {
      setDocsLoading(false);
    }
  }, [asset?.id]);

  useEffect(() => {
    if (activeTab === "documents") fetchDocs();
  }, [activeTab, fetchDocs]);

  useEffect(() => {
    if (asset?.images?.length) {
      loadImages();
    } else {
      setImages([]);
    }
  }, [asset]);

  const loadImages = async () => {
    if (!asset?.images?.length) return;
    const loaded = [];
    for (let i = 0; i < asset.images.length; i++) {
      try {
        const resp = await api.get(`/assets/${asset.id}/images/${i}`, { responseType: "blob" });
        loaded.push(URL.createObjectURL(resp.data));
      } catch { loaded.push(null); }
    }
    setImages(loaded);
  };

  const handleUploadImage = async (file) => {
    if (!file || !asset) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/assets/${asset.id}/upload-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onRefresh();
    } catch (err) {
      toast.error("Upload failed", { description: err.response?.data?.detail });
    } finally {
      setUploading(false);
    }
  };

  const handleAddMilestone = async (e) => {
    e.preventDefault();
    if (!msForm.amount && !msForm.percentage) return;
    setMsLoading(true);
    try {
      const amount = parseFloat(msForm.amount) || 0;
      const percentage = parseFloat(msForm.percentage);
      const price = asset.purchase_price || 0;
      await api.post(`/assets/${asset.id}/milestones`, {
        name: msForm.name,
        trigger: msForm.trigger,
        percentage: isNaN(percentage) ? null : percentage,
        date: msForm.date,
        amount: amount || (price && percentage ? Math.round(price * percentage) / 100 : 0),
        description: msForm.name || msForm.trigger,
        is_paid: msForm.is_paid,
      });
      setMsForm({ name: "", date: "", amount: "", percentage: "", trigger: "", is_paid: false });
      setShowAddMilestone(false);
      onRefresh();
    } catch (err) {
      toast.error("Failed to add milestone", { description: err.response?.data?.detail });
    } finally {
      setMsLoading(false);
    }
  };

  const handleTogglePaid = async (milestoneId, currentPaid) => {
    try {
      await api.put(`/assets/${asset.id}/milestones/${milestoneId}`, { is_paid: !currentPaid });
      onRefresh();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteMilestone = async (milestoneId) => {
    try {
      await api.delete(`/assets/${asset.id}/milestones/${milestoneId}`);
      onRefresh();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    setRemindersEnabled(!!asset?.payment_reminders_enabled);
  }, [asset?.id, asset?.payment_reminders_enabled]);

  const handleToggleReminders = async () => {
    if (!asset?.id || remindersBusy) return;
    const next = !remindersEnabled;
    setRemindersEnabled(next); // optimistic
    setRemindersBusy(true);
    try {
      await api.put(`/assets/${asset.id}/payment-reminders`, { enabled: next });
      toast.success(next ? "Email reminders enabled" : "Email reminders disabled", {
        description: next
          ? "We'll email you 30 / 14 / 7 / 1 days before each unpaid milestone."
          : "You'll no longer receive automated reminders for this property.",
      });
      onRefresh?.();
    } catch (err) {
      setRemindersEnabled(!next); // rollback
      toast.error("Could not update reminder setting", {
        description: err.response?.data?.detail || "Please try again.",
      });
    } finally {
      setRemindersBusy(false);
    }
  };

  const startEditName = () => {
    setNameDraft(asset?.name || "");
    setEditingName(true);
  };

  const saveName = async () => {
    const trimmed = (nameDraft || "").trim();
    if (!trimmed) {
      toast.error("Asset name cannot be empty");
      return;
    }
    if (trimmed === asset?.name) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      await api.put(`/assets/${asset.id}`, { name: trimmed });
      toast.success("Asset name updated");
      setEditingName(false);
      if (typeof onRefresh === "function") await onRefresh();
    } catch (err) {
      toast.error("Could not rename asset", {
        description: err.response?.data?.detail || "Please try again.",
      });
    } finally {
      setSavingName(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  });

  // Cleanup blob URLs
  useEffect(() => {
    return () => images.forEach(url => url && URL.revokeObjectURL(url));
  }, [images]);

  if (!asset) return null;

  // `localOverride` lets the user temporarily force native-currency display
  // for this asset, regardless of the global home currency.
  //   null     → use global home currency (display())
  //   "LOCAL"  → force asset's native currency
  const effectiveMode = localOverride === "LOCAL" ? "LOCAL" : "HOME";
  const assetCurrency = asset.currency || "EUR";
  const sym = effectiveMode === "LOCAL"
    ? (SYMBOLS[assetCurrency] || assetCurrency)
    : (SYMBOLS[mode] || mode);
  const roi = asset.roi || {};
  const isDubai = asset.location === "dubai";
  const isCyprus = asset.location === "north_cyprus";
  const isGermany = asset.location === "germany";

  // Spekulationsfrist — 10-year tax-free holding period (Germany)
  const speculation = (() => {
    if (!isGermany || !asset.purchase_date) return null;
    const purchase = new Date(asset.purchase_date + "T00:00:00");
    const end = new Date(purchase);
    end.setFullYear(end.getFullYear() + 10);
    const now = new Date();
    const msLeft = end - now;
    const isDone = msLeft <= 0;
    const totalDays = Math.floor((end - purchase) / (1000 * 60 * 60 * 24));
    const elapsedDays = Math.min(totalDays, Math.max(0, Math.floor((now - purchase) / (1000 * 60 * 60 * 24))));
    const progress = Math.min(100, Math.max(0, (elapsedDays / totalDays) * 100));
    const yearsLeft = Math.max(0, Math.floor(msLeft / (1000 * 60 * 60 * 24 * 365.25)));
    const monthsLeft = Math.max(0, Math.floor((msLeft % (1000 * 60 * 60 * 24 * 365.25)) / (1000 * 60 * 60 * 24 * 30.4375)));
    return { end, isDone, progress, yearsLeft, monthsLeft };
  })();

  // Format helper that respects effective mode for this modal
  const f = (val) => {
    if (val == null) return "—";
    if (effectiveMode === "LOCAL") {
      return `${SYMBOLS[assetCurrency] || assetCurrency} ${(val || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    }
    const d = display(val, assetCurrency);
    return `${d.symbol} ${d.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };
  const fRaw = (val) => {
    if (val == null) return 0;
    if (effectiveMode === "LOCAL") return val;
    return display(val, assetCurrency).value;
  };

  // Milestones
  const milestones = asset.payment_milestones || [];
  const sortedMilestones = [...milestones].sort((a, b) => (a.date || "9999").localeCompare(b.date || "9999"));
  const totalAmount = milestones.reduce((s, m) => s + (m.amount || 0), 0);
  const totalPaid = milestones.filter(m => m.is_paid).reduce((s, m) => s + (m.amount || 0), 0);
  const totalRemaining = Math.max(0, totalAmount - totalPaid);
  const paidCount = milestones.filter(m => m.is_paid).length;
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const nextDue = sortedMilestones.find(m => !m.is_paid);  // first unpaid (earliest)
  const handleExtractPlan = async () => {
    if (!asset.source_document_id) {
      toast.warning("Link a Payment Plan document first", {
        description: "From the Document Vault, assign an SPA / Payment Plan PDF to this asset.",
      });
      return;
    }
    try {
      setMsLoading(true);
      await api.post(`/documents/${asset.source_document_id}/extract-payment-plan`, { asset_id: asset.id });
      onRefresh();
      toast.success("Payment plan extracted");
    } catch (err) {
      toast.error("Could not extract payment plan", { description: err.response?.data?.detail });
    } finally {
      setMsLoading(false);
    }
  };

  const handleExtractFromDoc = async (docId) => {
    setExtractingDocId(docId);
    try {
      const { data } = await api.post(`/documents/${docId}/extract-payment-plan`, { asset_id: asset.id });
      onRefresh();
      toast.success(`Extracted ${data.extracted_count} payment milestones`, {
        description: `${Math.round((data.confidence || 0) * 100)}% confidence — see the Payment Plan section.`,
      });
    } catch (err) {
      toast.error("Could not extract payment plan", { description: err.response?.data?.detail });
    } finally {
      setExtractingDocId(null);
    }
  };

  const handleClearPlan = () => {
    const count = milestones.length;
    if (!count) return;
    setConfirmClearPlan({
      open: true,
      onConfirm: async () => {
        setConfirmClearPlan({ open: false });
        try {
          await api.delete(`/assets/${asset.id}/milestones`);
          onRefresh();
          toast.success(`Removed ${count} milestone${count === 1 ? "" : "s"}`, {
            description: "The payment plan has been cleared.",
          });
        } catch (err) {
          toast.error("Could not clear payment plan", { description: err.response?.data?.detail });
        }
      },
    });
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 z-50" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            className="fixed inset-0 sm:inset-3 md:inset-6 z-50 bg-[#0A0A0A] border-t sm:border border-white/[0.12] overflow-y-auto"
            data-testid="asset-detail-modal"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            {/* Header */}
            <div className="sticky top-0 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-white/[0.10] px-4 md:px-6 py-3 md:py-4 flex items-start justify-between z-10 gap-3">
              <div className="min-w-0">
                <p className="text-[10px] tracking-[0.15em] uppercase text-[#D4AF37]">Property Detail</p>
                {editingName ? (
                  <div className="flex items-center gap-2 mt-0.5">
                    <input
                      type="text"
                      value={nameDraft}
                      autoFocus
                      data-testid="asset-name-input"
                      onChange={(e) => setNameDraft(e.target.value)}
                      onBlur={saveName}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveName();
                        if (e.key === "Escape") setEditingName(false);
                      }}
                      disabled={savingName}
                      maxLength={120}
                      className="font-serif text-lg md:text-xl text-white bg-transparent border-b border-[#D4AF37]/40 focus:border-[#D4AF37] outline-none px-1 py-0.5 min-w-[200px] flex-1 max-w-[420px]"
                    />
                    {savingName && <span className="text-[10px] text-white/60 uppercase tracking-wider">Saving…</span>}
                  </div>
                ) : (
                  <h2
                    className="font-serif text-lg md:text-xl text-white mt-0.5 truncate cursor-text hover:bg-white/[0.03] -mx-1 px-1 transition-colors inline-block"
                    onClick={startEditName}
                    title="Click to rename"
                    data-testid="asset-name-display"
                  >
                    {asset.name}
                  </h2>
                )}
                <div className="flex items-center gap-2 md:gap-3 mt-1 flex-wrap">
                  <span className="flex items-center gap-1 text-white/60 text-xs"><MapPin className="w-3 h-3" />{locationLabels[asset.location] || asset.country_name || "Global"}{asset.city ? ` · ${asset.city}` : ""}</span>
                  <span className="text-white/55 text-[10px] uppercase">{asset.property_type?.replace("_", " ")}</span>
                  {asset.area_sqft && <span className="text-white/55 text-[10px]">{asset.area_sqft} {isCyprus || isGermany ? "m\u00b2" : "sqft"}</span>}
                  {asset.purchase_date && (
                    <span
                      className="flex items-center gap-1 text-white/70 text-[11px]"
                      data-testid="asset-purchase-date-display"
                      title="Purchase date"
                    >
                      <CalendarDays className="w-3 h-3 text-[#D4AF37]/80" />
                      <span className="text-[10px] tracking-[0.12em] uppercase text-white/45">Bought</span>
                      {(() => {
                        try {
                          return formatDate(
                            new Date(asset.purchase_date + "T00:00:00"),
                            "MMM dd, yyyy"
                          );
                        } catch {
                          return asset.purchase_date;
                        }
                      })()}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => setEditAssetOpen(true)}
                  data-testid="edit-asset-btn"
                  className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase text-[#D4AF37]/80 hover:text-[#D4AF37] border border-[#D4AF37]/30 hover:border-[#D4AF37]/60 px-3 py-1.5 transition-colors"
                  title="Edit this asset (price, costs, financing, renovation…)"
                >
                  <Pencil className="w-3 h-3" />
                  Edit
                </button>
                <button onClick={onClose} className="text-white/65 hover:text-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center -mr-2" data-testid="close-detail-btn">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4 md:p-6 space-y-5 md:space-y-6">
              {/* Contextual Currency Switch */}
              <div className="flex items-center justify-end mb-1">
                <button
                  onClick={() => setLocalOverride(prev => prev === "LOCAL" ? null : "LOCAL")}
                  data-testid="detail-currency-switch"
                  className="flex items-center gap-1.5 text-white/55 hover:text-[#D4AF37] transition-colors text-[10px] font-mono"
                  title={
                    effectiveMode === "LOCAL"
                      ? `Showing in ${assetCurrency} (asset native) — click to switch back to ${mode}`
                      : `Showing in ${mode} (home) — click to switch to ${assetCurrency}`
                  }
                >
                  <ArrowLeftRight className="w-3 h-3" />
                  {effectiveMode === "LOCAL" ? assetCurrency : mode}
                </button>
              </div>

              {/* Tabs */}
              <div className="flex items-center gap-6 border-b border-white/[0.10]">
                <button
                  onClick={() => setActiveTab("overview")}
                  data-testid="asset-tab-overview"
                  className={`pb-2 text-xs tracking-[0.12em] uppercase transition-colors border-b-2 ${
                    activeTab === "overview" ? "text-[#D4AF37] border-[#D4AF37]" : "text-white/60 hover:text-white/90 border-transparent"
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab("documents")}
                  data-testid="asset-tab-documents"
                  className={`pb-2 text-xs tracking-[0.12em] uppercase transition-colors border-b-2 flex items-center gap-2 ${
                    activeTab === "documents" ? "text-[#D4AF37] border-[#D4AF37]" : "text-white/60 hover:text-white/90 border-transparent"
                  }`}
                >
                  Documents
                  {docs.length > 0 && <span className="font-mono text-[10px] text-white/60">{docs.length}</span>}
                </button>
              </div>

              {activeTab === "overview" && (<>

              {/* KPI Summary Bar */}
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white/[0.02] border border-white/[0.10] p-4">
                  <p className="text-[9px] tracking-[0.12em] uppercase text-white/65 mb-1">Value</p>
                  <p className="font-mono text-lg text-white">{f(asset.current_value_eur)}</p>
                </div>
                <div className="bg-white/[0.02] border border-white/[0.10] p-4">
                  <p className="text-[9px] tracking-[0.12em] uppercase text-white/65 mb-1">Monthly Net</p>
                  <p className={`font-mono text-lg ${(fRaw(roi.monthly_cashflow) >= 0) ? "text-emerald-400" : "text-red-400"}`}>{f(roi.monthly_cashflow)}</p>
                </div>
                <div className="bg-white/[0.02] border border-white/[0.10] p-4">
                  <p className="text-[9px] tracking-[0.12em] uppercase text-white/65 mb-1">Equity Yield</p>
                  <p className="font-mono text-lg text-[#D4AF37]">{roi.equity_yield_pct == null ? "—" : `${roi.equity_yield_pct}%`}</p>
                </div>
                <div className="bg-white/[0.02] border border-white/[0.10] p-4">
                  <p className="text-[9px] tracking-[0.12em] uppercase text-white/65 mb-1">Gross ROI</p>
                  <p className="font-mono text-lg text-[#D4AF37]">{roi.total_roi_pct || 0}%</p>
                </div>
              </div>

              {/* Spekulationsfrist — 10-year tax-free holding period (Germany only) */}
              {speculation && (
                <div className="bg-white/[0.02] border border-[#D4AF37]/20 p-5" data-testid="spekulationsfrist-widget">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <p className="text-[10px] tracking-[0.15em] uppercase text-[#D4AF37] mb-1">Spekulationsfrist</p>
                      <p className="text-white text-sm font-serif">10-Year Tax-Free Holding Period</p>
                    </div>
                    {speculation.isDone ? (
                      <span className="text-[10px] tracking-[0.1em] uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2.5 py-1" data-testid="spekulationsfrist-status">
                        Tax-Free Sale Unlocked
                      </span>
                    ) : (
                      <span className="text-[10px] tracking-[0.1em] uppercase bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2.5 py-1 font-mono" data-testid="spekulationsfrist-status">
                        {speculation.yearsLeft}y {speculation.monthsLeft}m remaining
                      </span>
                    )}
                  </div>
                  <div className="h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${speculation.isDone ? "bg-emerald-400" : "bg-[#D4AF37]"}`}
                      style={{ width: `${speculation.progress}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-2 text-[10px] text-white/65 font-mono">
                    <span>Purchase: {asset.purchase_date}</span>
                    <span>Tax-free from: {speculation.end.toISOString().slice(0, 10)}</span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Purchase Cost Breakdown */}
                <div className="bg-white/[0.02] border border-white/[0.10] p-5">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-4">Purchase Cost Breakdown</p>
                  <div className="space-y-2.5">
                    <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                      <span className="text-white/65 text-sm">Purchase Price</span>
                      <span className="font-mono text-sm text-white">{f(asset.purchase_price)}</span>
                    </div>
                    {isDubai && (
                      <>
                        <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                          <span className="text-white/65 text-sm">DLD Transfer Fee (4%)</span>
                          <span className="font-mono text-sm text-white/90">{f(asset.dld_fee || roi.dld_fee)}</span>
                        </div>
                        <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                          <span className="text-white/65 text-sm">Brokerage Fees</span>
                          <span className="font-mono text-sm text-white/90">{f(asset.broker_commission || roi.broker_commission)}</span>
                        </div>
                        <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                          <span className="text-white/65 text-sm">Trustee Office Fee</span>
                          <span className="font-mono text-sm text-white/90">{f(asset.trustee_fee || roi.trustee_fee)}</span>
                        </div>
                      </>
                    )}
                    {isCyprus && (
                      <>
                        <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                          <span className="text-white/65 text-sm">Stamp Duty (0.5%)</span>
                          <span className="font-mono text-sm text-white/90">{f(asset.stamp_duty ?? roi.stamp_duty)}</span>
                        </div>
                        {(asset.vat ?? roi.vat) > 0 && (
                          <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                            <span className="text-white/65 text-sm">VAT (5%)</span>
                            <span className="font-mono text-sm text-white/90">{f(asset.vat ?? roi.vat)}</span>
                          </div>
                        )}
                        <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                          <span className="text-white/65 text-sm">Title Deed Transfer (12%)</span>
                          <span className="font-mono text-sm text-white/90">{f(asset.transfer_fee ?? roi.transfer_fee)}</span>
                        </div>
                        {(asset.transformer_fee ?? roi.transformer_fee) > 0 && (
                          <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                            <span className="text-white/65 text-sm">Transformer Fee</span>
                            <span className="font-mono text-sm text-white/90">{f(asset.transformer_fee ?? roi.transformer_fee)}</span>
                          </div>
                        )}
                        {(asset.solicitor_fee ?? roi.solicitor_fee) > 0 && (
                          <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                            <span className="text-white/65 text-sm">Solicitor / Legal Fees</span>
                            <span className="font-mono text-sm text-white/90">{f(asset.solicitor_fee ?? roi.solicitor_fee)}</span>
                          </div>
                        )}
                      </>
                    )}
                    {isGermany && (
                      <>
                        <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                          <span className="text-white/65 text-sm">Grunderwerbsteuer ({roi.grunderwerbsteuer_rate_pct ? `${roi.grunderwerbsteuer_rate_pct}%` : "custom"}{asset.bundesland ? ` \u2022 ${BUNDESLAND_LABELS[asset.bundesland] || asset.bundesland}` : ""})</span>
                          <span className="font-mono text-sm text-white/90">{f(asset.grunderwerbsteuer ?? roi.grunderwerbsteuer)}</span>
                        </div>
                        <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                          <span className="text-white/65 text-sm">Notar- &amp; Gerichtskosten</span>
                          <span className="font-mono text-sm text-white/90">{f(asset.notary_fee ?? roi.notary_fee)}</span>
                        </div>
                        <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                          <span className="text-white/65 text-sm">Maklerprovision (3.57%)</span>
                          <span className="font-mono text-sm text-white/90">{f(asset.broker_commission ?? roi.broker_commission)}</span>
                        </div>
                        {(asset.renovation_cost ?? roi.renovation_cost) > 0 && (
                          <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                            <span className="text-white/65 text-sm">Renovierung &amp; Sanierung</span>
                            <span className="font-mono text-sm text-white/90">{f(asset.renovation_cost ?? roi.renovation_cost)}</span>
                          </div>
                        )}
                      </>
                    )}
                    {asset.other_purchase_costs > 0 && (
                      <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                        <span className="text-white/65 text-sm">Other Purchase Costs</span>
                        <span className="font-mono text-sm text-white/90">{f(asset.other_purchase_costs)}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 border-t border-[#D4AF37]/20 mt-2">
                      <span className="text-white text-sm font-medium">Total Investment</span>
                      <span className="font-mono text-sm text-[#D4AF37] font-medium">{f(asset.total_investment || roi.total_investment)}</span>
                    </div>
                  </div>
                </div>

                {/* Rental & ROI Breakdown */}
                <div className="bg-white/[0.02] border border-white/[0.10] p-5">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-4">Rental &amp; ROI Analysis</p>
                  <div className="space-y-2.5">
                    <div className="flex justify-between items-center py-1.5 border-b border-white/[0.08] group">
                      <span className="text-white/65 text-sm">Monthly Rental Income</span>
                      {editingRental ? (
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-xs text-white/60">{SYMBOLS[assetCurrency] || assetCurrency}</span>
                          <input
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="any"
                            autoFocus
                            value={rentalDraft}
                            onChange={(e) => setRentalDraft(e.target.value)}
                            onBlur={async () => {
                              const next = rentalDraft === "" ? null : parseFloat(rentalDraft);
                              if (next !== null && (isNaN(next) || next < 0)) {
                                toast.warning("Enter a valid amount");
                                setEditingRental(false);
                                return;
                              }
                              const current = asset.monthly_rental_income ?? null;
                              if (next === current) { setEditingRental(false); return; }
                              setEditingRental(false);
                              try {
                                await api.put(`/assets/${asset.id}`, { monthly_rental_income: next ?? 0 });
                                onRefresh();
                                toast.success("Rental income updated", {
                                  description: `${assetCurrency} ${(next ?? 0).toLocaleString()} / month`,
                                });
                              } catch (err) {
                                toast.error("Could not update rental income", { description: err.response?.data?.detail });
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") e.target.blur();
                              if (e.key === "Escape") { setRentalDraft(String(asset.monthly_rental_income ?? "")); setEditingRental(false); }
                            }}
                            data-testid="rental-income-input"
                            className="w-32 bg-[#050505] border border-[#D4AF37]/40 text-white px-2 py-1 text-sm font-mono text-right focus:border-[#D4AF37] outline-none"
                            placeholder={`amount in ${assetCurrency}`}
                          />
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            // Always edit the LOCAL-currency value, regardless of display mode
                            setRentalDraft(String(asset.monthly_rental_income ?? ""));
                            setEditingRental(true);
                          }}
                          data-testid="rental-income-edit-btn"
                          className="font-mono text-sm text-white hover:text-[#D4AF37] transition-colors"
                          title={`Click to edit (in ${assetCurrency})`}
                        >
                          {f(asset.monthly_rental_income)}
                        </button>
                      )}
                    </div>
                    {editingRental && assetCurrency !== "EUR" && (
                      <p className="text-[10px] text-[#D4AF37]/60 -mt-1.5 mb-1.5 text-right">
                        Enter the amount in {assetCurrency}, the property's local currency.
                      </p>
                    )}
                    <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                      <span className="text-white/65 text-sm">Gross Annual Rental</span>
                      <span className="font-mono text-sm text-white/90">{f(roi.gross_annual_rental)}</span>
                    </div>

                    {/* Germany Hausgeld breakdown — surfaced inline so the user can
                        see exactly how much of the Hausgeld flows through to their
                        running costs (nicht-umlagefähig) vs. passes through to the
                        tenant (umlagefähig). Only shown for Germany assets that
                        actually have one of the two split fields set. */}
                    {isGermany && (roi.hausgeld_umlage_annual > 0 || roi.hausgeld_nicht_umlage_annual > 0 || asset.hausgeld_umlage_monthly || asset.hausgeld_nicht_umlage_monthly) && (
                      <div className="bg-[#D4AF37]/[0.03] border-l-2 border-[#D4AF37]/30 pl-3 py-2 my-1" data-testid="germany-hausgeld-breakdown">
                        <p className="text-[9px] tracking-[0.15em] uppercase text-[#D4AF37]/60 mb-1.5">Hausgeld Split (BetrKV)</p>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-white/65">Umlagef&auml;hig / yr <span className="text-white/60">(tenant pays)</span></span>
                            <span className="font-mono text-white/65">{f(roi.hausgeld_umlage_annual || 0)}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-[#D4AF37]/80">Nicht-umlagef&auml;hig / yr <span className="text-white/65">(you pay)</span></span>
                            <span className="font-mono text-[#D4AF37]/80">{f(roi.hausgeld_nicht_umlage_annual || 0)}</span>
                          </div>
                          {roi.instandhaltungsruecklage_annual > 0 && (
                            <div className="flex justify-between text-xs">
                              <span className="text-[#D4AF37]/80">Instandhaltungsr&uuml;cklage / yr</span>
                              <span className="font-mono text-[#D4AF37]/80">{f(roi.instandhaltungsruecklage_annual)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-between items-center py-1.5 border-b border-white/[0.08]">
                      <span className="text-white/65 text-sm">Running Costs (Annual)</span>
                      {editingRunCosts ? (
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-xs text-white/60">{SYMBOLS[assetCurrency] || assetCurrency}</span>
                          <input
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="any"
                            autoFocus
                            value={runCostsDraft}
                            onChange={(e) => setRunCostsDraft(e.target.value)}
                            onBlur={async () => {
                              const next = runCostsDraft === "" ? null : parseFloat(runCostsDraft);
                              if (next !== null && (isNaN(next) || next < 0)) {
                                toast.warning("Enter a valid amount");
                                setEditingRunCosts(false);
                                return;
                              }
                              const current = asset.running_costs_override ?? null;
                              if (next === current) { setEditingRunCosts(false); return; }
                              setEditingRunCosts(false);
                              try {
                                await api.put(`/assets/${asset.id}`, { running_costs_override: next ?? 0 });
                                onRefresh();
                                toast.success("Running costs updated", {
                                  description: `${assetCurrency} ${(next ?? 0).toLocaleString()} / year`,
                                });
                              } catch (err) {
                                toast.error("Could not update running costs", { description: err.response?.data?.detail });
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") e.target.blur();
                              if (e.key === "Escape") {
                                setRunCostsDraft(String(asset.running_costs_override ?? roi.running_costs_annual ?? ""));
                                setEditingRunCosts(false);
                              }
                            }}
                            data-testid="running-costs-input"
                            className="w-32 bg-[#050505] border border-[#D4AF37]/40 text-white px-2 py-1 text-sm font-mono text-right focus:border-[#D4AF37] outline-none"
                            placeholder={`amount in ${assetCurrency}`}
                          />
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setRunCostsDraft(String(asset.running_costs_override ?? roi.running_costs_annual ?? ""));
                            setEditingRunCosts(true);
                          }}
                          data-testid="running-costs-edit-btn"
                          className="font-mono text-sm text-red-400/70 hover:text-[#D4AF37] transition-colors"
                          title={`Click to edit annual running costs (in ${assetCurrency})`}
                        >
                          -{f(roi.running_costs_annual)}
                        </button>
                      )}
                    </div>
                    {editingRunCosts && assetCurrency !== "EUR" && (
                      <p className="text-[10px] text-[#D4AF37]/60 -mt-1.5 mb-1.5 text-right">
                        Enter the annual amount in {assetCurrency}, the property's local currency.
                      </p>
                    )}
                    <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                      <span className="text-white/65 text-sm">Annual Net Income (before financing)</span>
                      <span className="font-mono text-sm text-white/90">{f(roi.net_annual_before_financing)}</span>
                    </div>

                    {/* Financing Cost Breakdown */}
                    {roi.is_financed && (
                      <>
                        <div className="pt-1 pb-1">
                          <p className="text-[9px] tracking-[0.12em] uppercase text-amber-400/60 mb-2">Financing Costs</p>
                        </div>
                        <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                          <span className="text-white/65 text-sm">Loan Amount (Darlehensbetrag)</span>
                          <span className="font-mono text-sm text-white/90">{f(roi.loan_amount)}</span>
                        </div>
                        <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                          <span className="text-white/65 text-sm">Annual Interest ({asset.interest_rate_pct}%)</span>
                          <span className="font-mono text-sm text-red-400/70">-{f(roi.annual_interest)}</span>
                        </div>
                        {roi.annual_amortization > 0 && (
                          <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                            <span className="text-white/65 text-sm">Annual Amortization ({asset.amortization_pct}%)</span>
                            <span className="font-mono text-sm text-red-400/70">-{f(roi.annual_amortization)}</span>
                          </div>
                        )}
                        <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                          <span className="text-white/65 text-sm">Monthly Financing Cost</span>
                          <span className="font-mono text-sm text-red-400/70">-{f(roi.monthly_financing_cost)}</span>
                        </div>
                        {roi.annual_principal_repayment > 0 && (
                          <div
                            className="flex justify-between items-baseline py-1.5 border-b border-white/[0.08]"
                            data-testid="debt-paydown-row"
                            title="Equity built each year by repaying principal. Excluded from Equity Yield (EKR) but counted as a balance-sheet benefit."
                          >
                            <span className="text-white/65 text-sm flex items-center gap-1.5">
                              Debt Paydown
                              <span className="text-[9px] tracking-wider uppercase text-emerald-400/70 border border-emerald-500/20 px-1.5 py-0.5">Equity+</span>
                            </span>
                            <span className="font-mono text-sm text-emerald-400">+{f(roi.annual_principal_repayment)}/yr</span>
                          </div>
                        )}
                      </>
                    )}

                    <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                      <span className="text-white text-sm font-medium">Net Cashflow {roi.is_financed ? "(after financing)" : ""}</span>
                      <span className={`font-mono text-sm ${roi.yearly_cashflow >= 0 ? "text-emerald-400" : "text-red-400"}`}>{f(roi.yearly_cashflow)}/yr</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                      <span className="text-white/65 text-sm">Monthly Net Profit</span>
                      <span className={`font-mono text-sm ${roi.monthly_cashflow >= 0 ? "text-emerald-400" : "text-red-400"}`}>{f(roi.monthly_cashflow)}</span>
                    </div>

                    {roi.is_financed && (
                      <div className="flex justify-between py-1.5 border-b border-[#D4AF37]/10">
                        <span className="text-white/65 text-sm">Equity Invested</span>
                        <span className="font-mono text-sm text-white">{f(roi.equity_invested)}</span>
                      </div>
                    )}

                    <div className="flex justify-between py-1.5 border-b border-white/[0.08]">
                      <span className="text-white/65 text-sm">Gross ROI</span>
                      <span className="font-mono text-sm text-[#D4AF37]">{roi.total_roi_pct || 0}%</span>
                    </div>
                    <div className="flex justify-between py-2 border-t border-[#D4AF37]/20 mt-2">
                      <span className="text-white text-sm font-medium">Equity Yield</span>
                      <span className="font-mono text-sm text-[#D4AF37] font-medium">{roi.equity_yield_pct == null ? "—" : `${roi.equity_yield_pct}%`}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              {asset.description && (
                <div className="bg-white/[0.02] border border-white/[0.10] p-5">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-2">Description</p>
                  <p className="text-white/85 text-sm leading-relaxed">{asset.description}</p>
                </div>
              )}

              {/* Payment Milestones */}
              <div className="bg-white/[0.02] border border-white/[0.10] p-5" data-testid="milestones-section">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-white/60">Payment Plan</p>
                    {milestones.length > 0 && (
                      <p className="text-white/55 text-[10px] mt-0.5">
                        {paidCount}/{milestones.length} paid &middot; {f(totalPaid)} paid &middot; {f(totalRemaining)} remaining
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {asset.source_document_id && (
                      <button
                        onClick={handleExtractPlan}
                        disabled={msLoading}
                        data-testid="extract-plan-btn"
                        className="flex items-center gap-1.5 text-[#D4AF37]/70 text-xs hover:text-[#D4AF37] transition-colors disabled:opacity-50"
                        title="Re-run AI extraction on the linked SPA / Payment Plan"
                      >
                        <FileUp className="w-3.5 h-3.5" /> {msLoading ? "Extracting…" : "AI Extract"}
                      </button>
                    )}
                    <button
                      onClick={() => setShowAddMilestone(!showAddMilestone)}
                      data-testid="add-milestone-btn"
                      className="flex items-center gap-1.5 text-[#D4AF37]/70 text-xs hover:text-[#D4AF37] transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Milestone
                    </button>
                    {milestones.length > 0 && (
                      <button
                        onClick={handleClearPlan}
                        data-testid="clear-payment-plan-btn"
                        className="flex items-center gap-1.5 text-white/65 hover:text-red-400 text-xs transition-colors"
                        title="Delete the entire payment plan"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Email reminder opt-in */}
                {milestones.length > 0 && (
                  <div
                    className="mb-4 flex items-center justify-between gap-3 px-4 py-3 border border-white/[0.10] bg-white/[0.02]"
                    data-testid="payment-reminders-toggle-row"
                  >
                    <div className="min-w-0">
                      <p className="text-[10px] tracking-[0.15em] uppercase text-white/80 flex items-center gap-2">
                        Email Reminders
                        <span
                          className={`inline-block w-1.5 h-1.5 rounded-full ${remindersEnabled ? "bg-[#D4AF37]" : "bg-white/15"}`}
                          aria-hidden
                        />
                      </p>
                      <p className="text-[11px] text-white/55 mt-1 leading-relaxed">
                        {remindersEnabled
                          ? "Enabled · We'll email you 30 / 14 / 7 / 1 days before each unpaid milestone."
                          : "Off · Toggle on to receive automated payment-due emails for this property."}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleToggleReminders}
                      disabled={remindersBusy}
                      data-testid="payment-reminders-toggle"
                      role="switch"
                      aria-checked={remindersEnabled}
                      aria-label="Toggle email reminders for payment milestones"
                      className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 disabled:opacity-50 ${
                        remindersEnabled ? "bg-[#D4AF37]" : "bg-white/10"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                          remindersEnabled ? "left-[22px]" : "left-0.5"
                        }`}
                      />
                    </button>
                  </div>
                )}

                {/* Next Payment Due banner */}
                {nextDue && (
                  <div className="mb-4 bg-[#D4AF37]/[0.06] border border-[#D4AF37]/20 px-4 py-3 flex items-center justify-between gap-3" data-testid="next-payment-banner">
                    <div className="min-w-0">
                      <p className="text-[9px] tracking-[0.18em] uppercase text-[#D4AF37]/70 mb-0.5">Next Payment Due</p>
                      <p className="text-white text-sm font-medium truncate">
                        {nextDue.name || nextDue.description || "Next installment"}
                        {nextDue.percentage ? <span className="text-white/60 ml-2">&middot; {nextDue.percentage}%</span> : null}
                      </p>
                      {(nextDue.trigger || nextDue.date) && (
                        <p className="text-white/60 text-[11px] truncate">
                          {nextDue.date && <span className="font-mono">{nextDue.date}</span>}
                          {nextDue.date && nextDue.trigger && <span> &middot; </span>}
                          {nextDue.trigger && <span>{nextDue.trigger}</span>}
                        </p>
                      )}
                    </div>
                    <span className="font-mono text-[#D4AF37] text-base flex-shrink-0">{f(nextDue.amount)}</span>
                  </div>
                )}

                {/* Progress bar */}
                {milestones.length > 0 && totalAmount > 0 && (
                  <div className="mb-5">
                    <div className="h-1.5 bg-white/[0.04] w-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min((totalPaid / totalAmount) * 100, 100)}%` }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        className="h-full bg-[#D4AF37]"
                      />
                    </div>
                    <p className="text-right text-[10px] text-white/60 mt-1 font-mono">
                      {totalAmount > 0 ? Math.round((totalPaid / totalAmount) * 100) : 0}%
                    </p>
                  </div>
                )}

                {/* Add milestone form */}
                <AnimatePresence>
                  {showAddMilestone && (
                    <motion.form
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      onSubmit={handleAddMilestone}
                      className="mb-4 bg-[#050505] border border-white/[0.10] p-4 space-y-3"
                      data-testid="add-milestone-form"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <label className="text-[10px] tracking-[0.12em] uppercase text-white/65 mb-1 block">Milestone Name</label>
                          <input type="text" value={msForm.name} onChange={e => setMsForm(p => ({...p, name: e.target.value}))}
                            data-testid="milestone-name-input"
                            className="w-full bg-[#0A0A0A] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" placeholder="e.g. Booking Deposit, On Handover, Installment #3" />
                        </div>
                        <div>
                          <label className="text-[10px] tracking-[0.12em] uppercase text-white/65 mb-1 block">Percentage (%)</label>
                          <input type="number" step="0.01" value={msForm.percentage} onChange={e => setMsForm(p => ({...p, percentage: e.target.value}))}
                            data-testid="milestone-pct-input"
                            className="w-full bg-[#0A0A0A] border border-white/10 text-white px-3 py-2 text-sm font-mono focus:border-[#D4AF37] outline-none" placeholder="e.g. 10" />
                        </div>
                        <div>
                          <label className="text-[10px] tracking-[0.12em] uppercase text-white/65 mb-1 block">Amount ({sym})</label>
                          <input type="number" step="0.01" value={msForm.amount} onChange={e => setMsForm(p => ({...p, amount: e.target.value}))}
                            data-testid="milestone-amount-input"
                            className="w-full bg-[#0A0A0A] border border-white/10 text-white px-3 py-2 text-sm font-mono focus:border-[#D4AF37] outline-none" placeholder="auto from %" />
                        </div>
                        <div>
                          <label className="text-[10px] tracking-[0.12em] uppercase text-white/65 mb-1 block">Estimated Date</label>
                          <input type="date" value={msForm.date} onChange={e => setMsForm(p => ({...p, date: e.target.value}))}
                            data-testid="milestone-date-input"
                            className="w-full bg-[#0A0A0A] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
                        </div>
                        <div>
                          <label className="text-[10px] tracking-[0.12em] uppercase text-white/65 mb-1 block">Trigger</label>
                          <input type="text" value={msForm.trigger} onChange={e => setMsForm(p => ({...p, trigger: e.target.value}))}
                            data-testid="milestone-trigger-input"
                            className="w-full bg-[#0A0A0A] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" placeholder="e.g. On 40% construction progress" />
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={msForm.is_paid} onChange={e => setMsForm(p => ({...p, is_paid: e.target.checked}))}
                            className="accent-[#D4AF37]" data-testid="milestone-paid-checkbox" />
                          <span className="text-white/60 text-xs">Already paid</span>
                        </label>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setShowAddMilestone(false)}
                            className="px-3 py-1.5 text-xs text-white/65 hover:text-white border border-white/10 transition-colors">Cancel</button>
                          <button type="submit" disabled={msLoading} data-testid="save-milestone-btn"
                            className="px-4 py-1.5 text-xs bg-[#D4AF37] text-black hover:bg-[#B5952F] transition-colors disabled:opacity-50">
                            {msLoading ? "Saving..." : "Add"}
                          </button>
                        </div>
                      </div>
                    </motion.form>
                  )}
                </AnimatePresence>

                {/* Timeline */}
                {milestones.length > 0 ? (
                  <div className="space-y-0">
                    {sortedMilestones.map((m, i) => {
                      const due = m.date ? new Date(m.date) : null;
                      const isPast = due && due < now;
                      const isOverdue = isPast && !m.is_paid;
                      const isUpcoming = !m.is_paid && !isOverdue;
                      const statusLabel = m.is_paid ? "Paid" : isOverdue ? "Overdue" : "Upcoming";
                      const statusCls = m.is_paid
                        ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                        : isOverdue
                          ? "bg-red-500/10 text-red-400 border-red-500/20"
                          : "bg-amber-500/10 text-amber-400 border-amber-500/20";
                      return (
                        <div key={m.id} className="flex gap-3" data-testid={`milestone-${m.id}`}>
                          {/* Timeline line + dot */}
                          <div className="flex flex-col items-center w-6 flex-shrink-0">
                            <button
                              onClick={() => handleTogglePaid(m.id, m.is_paid)}
                              data-testid={`toggle-paid-${m.id}`}
                              className="relative z-10 transition-colors"
                              title={m.is_paid ? "Mark as unpaid" : "Mark as paid"}
                            >
                              {m.is_paid ? (
                                <CircleCheck className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                              ) : isOverdue ? (
                                <CircleDot className="w-5 h-5 text-red-400" strokeWidth={1.5} />
                              ) : (
                                <Circle className="w-5 h-5 text-white/15" strokeWidth={1.5} />
                              )}
                            </button>
                            {i < sortedMilestones.length - 1 && (
                              <div className={`w-px flex-1 min-h-[44px] ${m.is_paid ? "bg-[#D4AF37]/30" : "bg-white/[0.06]"}`} />
                            )}
                          </div>

                          {/* Content */}
                          <div className="flex-1 pb-5 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className={`text-sm ${m.is_paid ? "text-white/60 line-through" : isOverdue ? "text-red-400" : "text-white"}`}>
                                    {m.name || m.description || "Payment"}
                                  </p>
                                  {m.percentage != null && (
                                    <span className="text-[10px] font-mono text-[#D4AF37]/80 bg-[#D4AF37]/[0.08] px-1.5 py-0.5">{m.percentage}%</span>
                                  )}
                                  <span className={`text-[9px] tracking-[0.12em] uppercase border px-1.5 py-0.5 ${statusCls}`}>
                                    {statusLabel}
                                  </span>
                                </div>
                                {(m.trigger || m.date) && (
                                  <p className="text-[10px] text-white/65 mt-1 truncate">
                                    {m.date && <span className="font-mono text-white/60">{m.date}</span>}
                                    {m.date && m.trigger && <span className="mx-1.5">&middot;</span>}
                                    {m.trigger && <span>{m.trigger}</span>}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                  onClick={() => handleTogglePaid(m.id, m.is_paid)}
                                  data-testid={`mark-paid-${m.id}`}
                                  className={`text-[10px] tracking-[0.1em] uppercase px-2.5 py-1 border transition-colors ${
                                    m.is_paid
                                      ? "border-white/15 text-white/65 hover:border-white/30 hover:text-white"
                                      : isOverdue
                                        ? "border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 hover:border-red-500/50"
                                        : "border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/50"
                                  }`}
                                  title={m.is_paid ? "Mark this installment as unpaid" : "Mark this installment as paid"}
                                >
                                  {m.is_paid ? "Mark Unpaid" : "Mark Paid"}
                                </button>
                                <span className={`font-mono text-sm ${m.is_paid ? "text-white/65" : isOverdue ? "text-red-400" : "text-white"}`}>
                                  {f(m.amount)}
                                </span>
                                <button onClick={() => handleDeleteMilestone(m.id)} data-testid={`delete-milestone-${m.id}`}
                                  className="text-white/10 hover:text-red-400 transition-colors p-0.5">
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 border border-dashed border-white/[0.12]">
                    <CalendarDays className="w-8 h-8 text-white/10 mx-auto mb-2" strokeWidth={1} />
                    <p className="text-white/60 text-sm mb-1">No payment plan linked yet</p>
                    <p className="text-white/60 text-xs mb-3">Upload your Payment Plan to see your detailed installment schedule here.</p>
                    <button
                      onClick={() => setActiveTab("documents")}
                      data-testid="empty-upload-plan-btn"
                      className="inline-flex items-center gap-1.5 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 border border-[#D4AF37]/30 text-[#D4AF37] text-xs px-4 py-2 transition-colors"
                    >
                      <Upload className="w-3.5 h-3.5" /> Upload Payment Plan
                    </button>
                  </div>
                )}
              </div>

              {/* Property Images */}
              <div className="bg-white/[0.02] border border-white/[0.10] p-4 md:p-5">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-white/60">Property Images</p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => cameraRef.current?.click()}
                      disabled={uploading}
                      data-testid="capture-asset-image-btn"
                      className="md:hidden flex items-center gap-1.5 text-[#D4AF37]/70 text-xs hover:text-[#D4AF37] transition-colors disabled:opacity-50 min-h-[36px]"
                    >
                      <Upload className="w-3.5 h-3.5 rotate-180" /> Camera
                    </button>
                    <button
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      data-testid="upload-asset-image-btn"
                      className="flex items-center gap-1.5 text-[#D4AF37]/70 text-xs hover:text-[#D4AF37] transition-colors disabled:opacity-50 min-h-[36px]"
                    >
                      <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading..." : "Add Photo"}
                    </button>
                  </div>
                  <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden"
                    onChange={e => handleUploadImage(e.target.files?.[0])} data-testid="asset-image-file-input" />
                  {/* Camera-only path: opens phone's back camera directly via `capture` attr.
                      Falls back to a file picker on desktop, which is fine since the
                      camera button is hidden on md+. */}
                  <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={e => handleUploadImage(e.target.files?.[0])} data-testid="asset-image-camera-input" />
                </div>

                {images.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3">
                    {images.map((url, i) => url && (
                      <div key={i} className="aspect-[4/3] bg-[#050505] border border-white/[0.10] overflow-hidden">
                        <img src={url} alt={`Property ${i + 1}`} className="w-full h-full object-cover" data-testid={`asset-image-${i}`} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 border border-dashed border-white/[0.12] cursor-pointer hover:border-white/[0.15] transition-colors"
                    onClick={() => fileRef.current?.click()}>
                    <ImageIcon className="w-8 h-8 text-white/10 mx-auto mb-2" strokeWidth={1} />
                    <p className="text-white/55 text-xs">Click to upload property photos</p>
                  </div>
                )}
              </div>

              </>)}

              {/* ===== DOCUMENTS TAB ===== */}
              {activeTab === "documents" && (
                <div className="space-y-3" data-testid="asset-documents-tab">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] tracking-[0.15em] uppercase text-white/60">Linked Documents</p>
                    <button
                      onClick={() => docFileRef.current?.click()}
                      disabled={docUploading}
                      data-testid="asset-upload-doc-btn"
                      className="flex items-center gap-1.5 text-[#D4AF37]/70 text-xs hover:text-[#D4AF37] transition-colors disabled:opacity-50"
                    >
                      <Upload className="w-3.5 h-3.5" /> {docUploading ? "Uploading..." : "Upload & link"}
                    </button>
                    <input
                      ref={docFileRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      className="hidden"
                      onChange={async e => {
                        const file = e.target.files?.[0];
                        if (!file || !asset) return;
                        setDocUploading(true);
                        try {
                          const fd = new FormData();
                          fd.append("file", file);
                          const qs = new URLSearchParams({
                            auto_classify: "true",
                            related_asset_id: asset.id,
                          });
                          const { data: uploaded } = await api.post(
                            `/documents/upload?${qs.toString()}`,
                            fd,
                            { headers: { "Content-Type": "multipart/form-data" } }
                          );
                          await fetchDocs();
                          // Auto-extract payment plan if the AI classified it as one
                          const isPaymentDoc =
                            uploaded?.document_type === "payment_schedule" ||
                            uploaded?.document_type === "contract_spa";
                          if (isPaymentDoc && uploaded?.id) {
                            try {
                              const { data } = await api.post(
                                `/documents/${uploaded.id}/extract-payment-plan`,
                                { asset_id: asset.id }
                              );
                              toast.success(`Extracted ${data.extracted_count} payment milestones`, {
                                description: `${Math.round((data.confidence || 0) * 100)}% confidence — see the Payment Plan section.`,
                              });
                              onRefresh();
                            } catch (err) {
                              toast.warning("Document uploaded, but plan auto-extract failed", {
                                description: err.response?.data?.detail || "You can retry from the row's 'Extract Plan' button.",
                              });
                            }
                          } else {
                            toast.success("Document uploaded");
                          }
                        } catch (err) {
                          toast.error("Upload failed", { description: err.response?.data?.detail });
                        } finally {
                          setDocUploading(false);
                          // reset input so the same file can be re-selected
                          if (e.target) e.target.value = "";
                        }
                      }}
                      data-testid="asset-doc-file-input"
                    />
                  </div>

                  {docsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-5 h-5 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : docs.length > 0 ? (
                    <div className="space-y-2">
                      {docs.map(d => (
                        <div key={d.id} className="bg-white/[0.02] border border-white/[0.10] p-3 flex items-center justify-between gap-3 hover:border-white/[0.1] transition-colors"
                          data-testid={`asset-doc-${d.id}`}>
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <div className="w-8 h-8 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                              <FileText className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.5} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-white text-sm truncate">{d.original_filename}</p>
                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                <span className="text-[9px] tracking-wider uppercase bg-[#D4AF37]/10 text-[#D4AF37]/80 px-1.5 py-0.5">
                                  {d.document_type?.replace("_", " ") || "Other"}
                                </span>
                                <span className="text-white/55 text-[10px]">{new Date(d.created_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {(d.document_type === "payment_schedule" || d.document_type === "contract_spa") && (
                              <button
                                onClick={() => handleExtractFromDoc(d.id)}
                                disabled={extractingDocId === d.id}
                                className="p-1.5 text-[#D4AF37]/70 hover:text-[#D4AF37] transition-colors disabled:opacity-30"
                                data-testid={`asset-doc-extract-${d.id}`}
                                title="Extract payment plan from this document"
                              >
                                <Sparkles className={`w-4 h-4 ${extractingDocId === d.id ? "animate-pulse" : ""}`} />
                              </button>
                            )}
                            <button
                              onClick={async () => {
                                try {
                                  const resp = await api.get(`/documents/${d.id}/preview`, { responseType: "blob" });
                                  const url = URL.createObjectURL(resp.data);
                                  window.open(url, "_blank");
                                } catch { toast.error("Preview failed"); }
                              }}
                              className="p-1.5 text-white/65 hover:text-[#D4AF37] transition-colors"
                              data-testid={`asset-doc-preview-${d.id}`}
                              title="Preview"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  const resp = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
                                  const url = URL.createObjectURL(resp.data);
                                  const a = document.createElement("a");
                                  a.href = url; a.download = d.original_filename; a.click();
                                  URL.revokeObjectURL(url);
                                } catch { toast.error("Download failed"); }
                              }}
                              className="p-1.5 text-white/65 hover:text-white transition-colors"
                              data-testid={`asset-doc-download-${d.id}`}
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setUnlinkDocId(d.id)}
                              className="p-1.5 text-white/65 hover:text-red-400 transition-colors"
                              data-testid={`asset-doc-unlink-${d.id}`}
                              title="Unlink from this asset"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-10 border border-dashed border-white/[0.12]">
                      <FileText className="w-8 h-8 text-white/10 mx-auto mb-2" strokeWidth={1} />
                      <p className="text-white/65 text-sm mb-1">No documents linked yet</p>
                      <p className="text-white/55 text-xs">Upload SPA, Title Deed, Invoices &mdash; AI auto-links matching files.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>

          <ConfirmDialog
            open={confirmClearPlan.open}
            title="Clear payment plan?"
            description={`This will remove all ${milestones.length} milestone${milestones.length === 1 ? "" : "s"} on this asset. The linked document stays in your Vault. This action cannot be undone.`}
            confirmLabel="Clear Plan"
            tone="danger"
            onCancel={() => setConfirmClearPlan({ open: false })}
            onConfirm={() => confirmClearPlan.onConfirm?.()}
          />

          <ConfirmDialog
            open={!!unlinkDocId}
            title="Unlink this document?"
            description="The document will remain in your Vault but will no longer be linked to this asset."
            confirmLabel="Unlink"
            tone="danger"
            onCancel={() => setUnlinkDocId(null)}
            onConfirm={async () => {
              const id = unlinkDocId;
              setUnlinkDocId(null);
              try {
                await api.put(`/documents/${id}/assign`, { related_asset_id: null });
                await fetchDocs();
                toast.success("Document unlinked");
              } catch (err) {
                toast.error("Unlink failed", { description: err.response?.data?.detail });
              }
            }}
          />

          {/* Edit-Asset reuses the wizard in edit mode so the form, validation,
              and per-jurisdiction logic stay in a single source of truth. */}
          <AddAssetDialog
            open={editAssetOpen}
            asset={asset}
            onClose={() => setEditAssetOpen(false)}
            onSuccess={() => {
              setEditAssetOpen(false);
              onRefresh?.();
            }}
          />
        </>
      )}
    </AnimatePresence>
  );
}
